import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const ORG_TYPE_LABEL = {
    creator: 'Создатель',
    executor: 'Исполнитель'
};

const TaskManagerOrganizationsPage = () => {
    const [loading, setLoading] = useState(false);
    const [bundle, setBundle] = useState({
        organizations: [],
        project_links: [],
        work_links: [],
        members: [],
        projects: [],
        work_types: []
    });
    const [installers, setInstallers] = useState([]);
    const [tab, setTab] = useState('orgs');

    const [orgOpen, setOrgOpen] = useState(false);
    const [orgEditing, setOrgEditing] = useState(null);
    const [orgForm] = Form.useForm();

    const [linkOpen, setLinkOpen] = useState(false);
    const [linkForm] = Form.useForm();

    const [workOpen, setWorkOpen] = useState(false);
    const [workForm] = Form.useForm();

    const [memberOpen, setMemberOpen] = useState(false);
    const [memberForm] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data }, inst] = await Promise.all([
                tmApi.get('/api/task-manager/org-bundle'),
                tmApi
                    .get('/api/task-manager/lookups/installers')
                    .catch(() => ({ data: { data: [] } }))
            ]);
            setBundle({
                organizations: data.data?.organizations || [],
                project_links: data.data?.project_links || [],
                work_links: data.data?.work_links || [],
                members: data.data?.members || [],
                projects: data.data?.projects || [],
                work_types: data.data?.work_types || []
            });
            setInstallers(inst.data?.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки организаций'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const creators = useMemo(
        () =>
            bundle.organizations.filter(
                o => o.org_type === 'creator'
            ),
        [bundle.organizations]
    );
    const executors = useMemo(
        () =>
            bundle.organizations.filter(
                o => o.org_type === 'executor'
            ),
        [bundle.organizations]
    );

    const projectOptions = useMemo(
        () =>
            bundle.projects.map(p => ({
                value: p.id,
                label: p.human_name
            })),
        [bundle.projects]
    );

    const workTypeOptions = useMemo(
        () =>
            bundle.work_types.map(w => ({
                value: w.value || w.label,
                label: w.label || w.value
            })),
        [bundle.work_types]
    );

    const openOrgCreate = type => {
        setOrgEditing(null);
        orgForm.resetFields();
        orgForm.setFieldsValue({
            org_type: type,
            sort_order: 100,
            is_active: true,
            color: type === 'creator' ? '#1677ff' : '#389e0d'
        });
        setOrgOpen(true);
    };

    const openOrgEdit = record => {
        setOrgEditing(record);
        orgForm.setFieldsValue(record);
        setOrgOpen(true);
    };

    const saveOrg = async () => {
        try {
            const values = await orgForm.validateFields();
            if (orgEditing) {
                await tmApi.put(
                    `/api/task-manager/organizations/${orgEditing.id}`,
                    values
                );
                message.success('Организация обновлена');
            } else {
                await tmApi.post(
                    '/api/task-manager/organizations',
                    values
                );
                message.success('Организация создана');
            }
            setOrgOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const removeOrg = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/organizations/${id}`
            );
            message.success('Организация удалена');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const saveProjectLink = async () => {
        try {
            const values = await linkForm.validateFields();
            await tmApi.put('/api/task-manager/project-orgs', values);
            message.success('Связь с проектом сохранена');
            setLinkOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const removeProjectLink = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/project-orgs/${id}`
            );
            message.success('Связь удалена');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const saveWorkLink = async () => {
        try {
            const values = await workForm.validateFields();
            await tmApi.put('/api/task-manager/org-work-links', {
                ...values,
                project_id: values.project_id || 0
            });
            message.success('Связка по виду работ сохранена');
            setWorkOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const removeWorkLink = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/org-work-links/${id}`
            );
            message.success('Связка удалена');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const saveMember = async () => {
        try {
            const values = await memberForm.validateFields();
            await tmApi.put('/api/task-manager/org-members', values);
            message.success('Участник сохранён');
            setMemberOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const removeMember = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/org-members/${id}`
            );
            message.success('Участник удалён');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const orgColumns = [
        {
            title: 'Тип',
            dataIndex: 'org_type',
            width: 120,
            render: t => (
                <Tag color={t === 'creator' ? 'blue' : 'green'}>
                    {ORG_TYPE_LABEL[t] || t}
                </Tag>
            )
        },
        { title: 'System', dataIndex: 'system_name', width: 160 },
        { title: 'Название', dataIndex: 'human_name' },
        { title: 'ИНН', dataIndex: 'inn', width: 120 },
        {
            title: 'Активна',
            dataIndex: 'is_active',
            width: 90,
            render: v =>
                v ? <Tag color="green">да</Tag> : <Tag>нет</Tag>
        },
        {
            title: '',
            width: 110,
            render: (_, row) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openOrgEdit(row)}
                    />
                    <Popconfirm
                        title="Удалить организацию?"
                        onConfirm={() => removeOrg(row.id)}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const projectLinkColumns = [
        {
            title: 'Проект',
            render: (_, r) =>
                r.project?.human_name || `#${r.project_id}`
        },
        {
            title: 'Организация',
            render: (_, r) =>
                r.organization?.human_name ||
                `#${r.organization_id}`
        },
        {
            title: 'Роль',
            dataIndex: 'binding_role',
            width: 130,
            render: t => (
                <Tag color={t === 'creator' ? 'blue' : 'green'}>
                    {ORG_TYPE_LABEL[t] || t}
                </Tag>
            )
        },
        {
            title: '',
            width: 70,
            render: (_, row) => (
                <Popconfirm
                    title="Удалить связь?"
                    onConfirm={() => removeProjectLink(row.id)}
                >
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                    />
                </Popconfirm>
            )
        }
    ];

    const workLinkColumns = [
        {
            title: 'Создатель',
            render: (_, r) =>
                r.creatorOrg?.human_name ||
                `#${r.creator_org_id}`
        },
        {
            title: 'Исполнитель',
            render: (_, r) =>
                r.executorOrg?.human_name ||
                `#${r.executor_org_id}`
        },
        { title: 'Вид работ', dataIndex: 'work_type', width: 120 },
        {
            title: 'Проект',
            width: 160,
            render: (_, r) =>
                r.project_id
                    ? r.project?.human_name || `#${r.project_id}`
                    : 'Любой'
        },
        {
            title: '',
            width: 70,
            render: (_, row) => (
                <Popconfirm
                    title="Удалить связку?"
                    onConfirm={() => removeWorkLink(row.id)}
                >
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                    />
                </Popconfirm>
            )
        }
    ];

    const memberColumns = [
        {
            title: 'Организация-исполнитель',
            render: (_, r) =>
                r.organization?.human_name ||
                `#${r.organization_id}`
        },
        {
            title: 'Пользователь',
            render: (_, r) =>
                r.user_fio || r.user?.login || `#${r.user_id}`
        },
        {
            title: 'Роль',
            dataIndex: 'member_role',
            width: 120
        },
        {
            title: 'Видеть',
            dataIndex: 'can_view',
            width: 80,
            render: v => (v ? 'да' : 'нет')
        },
        {
            title: 'Брать',
            dataIndex: 'can_take',
            width: 80,
            render: v => (v ? 'да' : 'нет')
        },
        {
            title: 'Компетенции',
            dataIndex: 'work_types',
            render: values => {
                const list = Array.isArray(values) ? values : [];
                if (!list.length) {
                    return (
                        <Text type="secondary">все виды</Text>
                    );
                }
                return (
                    <Space wrap size={[4, 4]}>
                        {list.map(v => (
                            <Tag key={v}>
                                {workTypeOptions.find(
                                    o => o.value === v
                                )?.label || v}
                            </Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: '',
            width: 110,
            render: (_, row) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            memberForm.setFieldsValue({
                                organization_id:
                                    row.organization_id,
                                user_id: row.user_id,
                                member_role:
                                    row.member_role ||
                                    'installer',
                                can_view: row.can_view !== false,
                                can_take: row.can_take !== false,
                                is_active:
                                    row.is_active !== false,
                                site_ids: Array.isArray(
                                    row.site_ids
                                )
                                    ? row.site_ids
                                    : [],
                                work_types: Array.isArray(
                                    row.work_types
                                )
                                    ? row.work_types
                                    : []
                            });
                            setMemberOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Убрать участника?"
                        onConfirm={() => removeMember(row.id)}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Организации заявок
                        </Title>
                        <Text type="secondary">
                            Создатель (филиал) ↔ исполнитель по
                            виду работ, монтажники и проекты
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={load}
                    >
                        Обновить
                    </Button>
                </Space>

                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        {
                            key: 'orgs',
                            label: 'Организации',
                            children: (
                                <>
                                    <Space
                                        style={{
                                            marginBottom: 12
                                        }}
                                    >
                                        <Button
                                            type="primary"
                                            icon={
                                                <PlusOutlined />
                                            }
                                            onClick={() =>
                                                openOrgCreate(
                                                    'creator'
                                                )
                                            }
                                        >
                                            Создатель
                                        </Button>
                                        <Button
                                            icon={
                                                <PlusOutlined />
                                            }
                                            onClick={() =>
                                                openOrgCreate(
                                                    'executor'
                                                )
                                            }
                                        >
                                            Исполнитель
                                        </Button>
                                    </Space>
                                    <Table
                                        rowKey="id"
                                        loading={loading}
                                        dataSource={
                                            bundle.organizations
                                        }
                                        columns={orgColumns}
                                        pagination={false}
                                    />
                                </>
                            )
                        },
                        {
                            key: 'projects',
                            label: 'К проектам',
                            children: (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        style={{
                                            marginBottom: 12
                                        }}
                                        onClick={() => {
                                            linkForm.resetFields();
                                            linkForm.setFieldsValue(
                                                {
                                                    is_active: true
                                                }
                                            );
                                            setLinkOpen(true);
                                        }}
                                    >
                                        Привязать к проекту
                                    </Button>
                                    <Table
                                        rowKey="id"
                                        loading={loading}
                                        dataSource={
                                            bundle.project_links
                                        }
                                        columns={
                                            projectLinkColumns
                                        }
                                        pagination={false}
                                    />
                                </>
                            )
                        },
                        {
                            key: 'work',
                            label: 'По видам работ',
                            children: (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        style={{
                                            marginBottom: 12
                                        }}
                                        onClick={() => {
                                            workForm.resetFields();
                                            workForm.setFieldsValue(
                                                {
                                                    is_active: true,
                                                    project_id: undefined
                                                }
                                            );
                                            setWorkOpen(true);
                                        }}
                                    >
                                        Связка создатель →
                                        исполнитель
                                    </Button>
                                    <Table
                                        rowKey="id"
                                        loading={loading}
                                        dataSource={
                                            bundle.work_links
                                        }
                                        columns={workLinkColumns}
                                        pagination={false}
                                    />
                                </>
                            )
                        },
                        {
                            key: 'members',
                            label: 'Монтажники',
                            children: (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        style={{
                                            marginBottom: 12
                                        }}
                                        onClick={() => {
                                            memberForm.resetFields();
                                            memberForm.setFieldsValue(
                                                {
                                                    member_role:
                                                        'installer',
                                                    can_view: true,
                                                    can_take: true,
                                                    is_active: true,
                                                    site_ids: [],
                                                    work_types: []
                                                }
                                            );
                                            setMemberOpen(true);
                                        }}
                                    >
                                        Добавить монтажника
                                    </Button>
                                    <Table
                                        rowKey="id"
                                        loading={loading}
                                        dataSource={
                                            bundle.members
                                        }
                                        columns={memberColumns}
                                        pagination={false}
                                    />
                                </>
                            )
                        }
                    ]}
                />

                <Modal
                    open={orgOpen}
                    title={
                        orgEditing
                            ? `Организация #${orgEditing.id}`
                            : 'Новая организация'
                    }
                    onCancel={() => setOrgOpen(false)}
                    onOk={saveOrg}
                    destroyOnClose
                >
                    <Form form={orgForm} layout="vertical">
                        <Form.Item
                            name="org_type"
                            label="Тип"
                            rules={[{ required: true }]}
                        >
                            <Select
                                disabled={Boolean(orgEditing)}
                                options={[
                                    {
                                        value: 'creator',
                                        label: 'Организация-создатель'
                                    },
                                    {
                                        value: 'executor',
                                        label: 'Организация-исполнитель'
                                    }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="system_name"
                            label="System name"
                            rules={[{ required: true }]}
                        >
                            <Input
                                disabled={Boolean(orgEditing)}
                                placeholder="moscow_ne"
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Москва северо-восток" />
                        </Form.Item>
                        <Form.Item name="inn" label="ИНН">
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item name="color" label="Цвет">
                            <Input type="color" />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активна"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={linkOpen}
                    title="Привязка к проекту"
                    onCancel={() => setLinkOpen(false)}
                    onOk={saveProjectLink}
                    destroyOnClose
                >
                    <Form form={linkForm} layout="vertical">
                        <Form.Item
                            name="project_id"
                            label="Проект"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={projectOptions}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="binding_role"
                            label="Роль в проекте"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={[
                                    {
                                        value: 'creator',
                                        label: 'Создатель'
                                    },
                                    {
                                        value: 'executor',
                                        label: 'Исполнитель'
                                    }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, cur) =>
                                prev.binding_role !==
                                cur.binding_role
                            }
                        >
                            {() => {
                                const role =
                                    linkForm.getFieldValue(
                                        'binding_role'
                                    );
                                const opts = (
                                    role === 'executor'
                                        ? executors
                                        : creators
                                ).map(o => ({
                                    value: o.id,
                                    label: o.human_name
                                }));
                                return (
                                    <Form.Item
                                        name="organization_id"
                                        label="Организация"
                                        rules={[
                                            { required: true }
                                        ]}
                                    >
                                        <Select
                                            options={opts}
                                            showSearch
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={workOpen}
                    title="Исполнитель для создателя по виду работ"
                    onCancel={() => setWorkOpen(false)}
                    onOk={saveWorkLink}
                    destroyOnClose
                >
                    <Form form={workForm} layout="vertical">
                        <Form.Item
                            name="creator_org_id"
                            label="Организация-создатель"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={creators.map(o => ({
                                    value: o.id,
                                    label: o.human_name
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="executor_org_id"
                            label="Организация-исполнитель"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={executors.map(o => ({
                                    value: o.id,
                                    label: o.human_name
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="work_type"
                            label="Вид работ"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={workTypeOptions}
                                showSearch
                                optionFilterProp="label"
                                placeholder="АВР"
                            />
                        </Form.Item>
                        <Form.Item
                            name="project_id"
                            label="Проект (необязательно)"
                        >
                            <Select
                                allowClear
                                options={projectOptions}
                                placeholder="Любой проект"
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={memberOpen}
                    title="Монтажник в организации-исполнителе"
                    onCancel={() => setMemberOpen(false)}
                    onOk={saveMember}
                    destroyOnClose
                >
                    <Form form={memberForm} layout="vertical">
                        <Form.Item
                            name="organization_id"
                            label="Организация-исполнитель"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={executors.map(o => ({
                                    value: o.id,
                                    label: o.human_name
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="user_id"
                            label="Монтажник"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={installers.map(u => ({
                                    value: u.id,
                                    label: u.label || u.value
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item
                            name="member_role"
                            label="Роль в организации"
                        >
                            <Select
                                options={[
                                    {
                                        value: 'installer',
                                        label: 'Монтажник'
                                    },
                                    {
                                        value: 'manager',
                                        label: 'Менеджер орг.'
                                    }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="can_view"
                            label="Может видеть заявки"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="can_take"
                            label="Может брать заявки"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="site_ids"
                            label="Участки"
                            extra="Пусто = все участки организации"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={(bundle.sites || []).map(
                                    s => ({
                                        value: s.id,
                                        label: s.code
                                            ? `${s.name} (${s.code})`
                                            : s.name
                                    })
                                )}
                            />
                        </Form.Item>
                        <Form.Item
                            name="work_types"
                            label="Компетенции (виды работ)"
                            extra="Пусто = все виды работ"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={workTypeOptions}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerOrganizationsPage;
